import React, { useMemo } from "react";
import Storyline from "./Storyline/Storyline";
import Home from "./Home/Home";
import * as _ from "lodash";
import {
    Routes,
    Route,
    Navigate,
    useParams,
    Outlet,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { loadStoryline as _loadStoryline } from "../store/storyline/actions";
import { setMenuIsExpanded as _setMenuIsExpanded } from "../store/app/actions";
import { RootState, useThunkDispatch } from "../store";
import useQueryParams from "../shared/hooks/useQueryParams";
import clsx from "clsx";
import Sidenav from "../shared/components/Sidenav";
import SidenavMiniMap from "./SidenavMiniMap/SidenavMiniMap";
import NotFound from "../shared/pages/NotFound";
import * as BaseComponents from "../shared/components/CanvasComponents";
import * as StorylineComponents from "./components";
import { pluginComponents } from "../shared/services/pluginService";
import { JsxParserComponentsContext } from "../shared/providers/JsxParserComponentsProvider";
import { openWsConnection } from "../store/vscode/actions";
import { AnchorWrapper } from "../shared/components/AnchorWrapper";
import { createSelector } from "@reduxjs/toolkit";

const activeItemContent = <SidenavMiniMap />;

function NavigateToHomeOrDefaultPage(props: { defaultPageUrl?: string }) {
    const { defaultPageUrl } = props;

    return (
        defaultPageUrl ?
            <Navigate to={defaultPageUrl} /> :
            <Home />
    );
}

export function RenderStoryline(props: { loader: (id: string) => any }) {
    const { loader } = props;
    const { id } = useParams();
    const activeBranch = useSelector((store: RootState) => store.app.activeBranch);

    React.useEffect(() => {
        loader(id);
    }, [id, activeBranch]);

    return (
        <Storyline />
    );
}

export function useViewerState(loadStoryline: (id: string, parameterValues: Map<string, any>) => void, setMenuIsExpanded: (value: boolean) => { type: string; value: boolean; }) {
    const visibilityClassesSelector = createSelector(
        [
            (state: RootState) => state.storyline.parameterValues.get("isExport"),
            (state: RootState) => state.storyline.parameterValues.get("hideTitleBar"),
            (state: RootState) => state.storyline.parameterValues.get("hideMinimap"),
            (state: RootState) => state.storyline.parameterValues.get("hideCommentary")
        ],
        (isExport, hideTitleBar, hideMinimap, hideCommentary) => [
            isExport === "1" ? "is-export" : undefined,
            hideTitleBar === "1" ? "hide-titlebar" : undefined,
            hideMinimap === "1" ? "hide-minimap" : undefined,
            hideCommentary === "1" ? "hide-commentary" : undefined
        ]
    );
    const visibilityClasses = useSelector(visibilityClassesSelector);

    const queryParams = useQueryParams();

    const ensureCorrectStorylineIsLoaded = (loader: typeof loadStoryline) => (id: string) => {
        // If nav-filters-id parameter is passed in, fetch the parameter values from session storage and include those in the full parameter set...
        const navFiltersId = queryParams.get("nav-filters-id");
        const navFiltersParams = navFiltersId ?
            JSON.parse(sessionStorage.getItem(`nav-filters-${navFiltersId}`) ?? "{}") :
            {};

        const allParameters = new Map<string, any>([
            ...Object.entries(navFiltersParams),
            ...queryParams.entries()
        ]);

        if (allParameters.has("menu-expanded")) {
            const expandedParamValue = allParameters.get("menu-expanded");
            setMenuIsExpanded(expandedParamValue === 1 || expandedParamValue === "true");
        }

        loader(id, allParameters);
    };
    return { visibilityClasses, ensureCorrectStorylineIsLoaded };
}

interface ViewerProps {
    menuItems: RootState["app"]["menuItems"],
}

const Viewer: React.FC<ViewerProps> = ({ menuItems }) => {
    const storylineId = useSelector((state: RootState) => state.storyline.id);
    const defaultPageUrl = useSelector((state: RootState) => state.app?.settings?.defaultPageUrl);
    const isDeveloper = useSelector((state: RootState) => state.app.user.isDeveloper);

    const dispatch = useThunkDispatch();
    const loadStoryline = React.useCallback((id: string, parameterValues: Map<string, any>) => {
        return dispatch(_loadStoryline(id, parameterValues));
    }, []);
    const setMenuIsExpanded = React.useCallback((value: boolean) => {
        return dispatch(_setMenuIsExpanded(value));
    }, []);

    const components = useMemo(() => {
        const result = { ...BaseComponents, ...StorylineComponents, ...pluginComponents, a: AnchorWrapper } as Record<string, any>;
        window.__QERENT_COMPONENTS__ = result;
        return result;
    }, [pluginComponents]);
    const { visibilityClasses, ensureCorrectStorylineIsLoaded } = useViewerState(loadStoryline, setMenuIsExpanded);

    React.useEffect(() => {
        // Immediately try to open the VS Code WebSocket connection if the user is a developer...
        if (isDeveloper && window.location.hostname !== "documentation.dev.qerent.ai") {
            dispatch(openWsConnection(components));
        }
    }, []);

    return (
        <JsxParserComponentsContext.Provider value={components}>
            <Routes>
                <Route element={
                    <div id={`storyline-${storylineId}`} className="app-layout storyline-page">
                        <Sidenav menuItems={menuItems} activeItemContent={activeItemContent} className={clsx(visibilityClasses)} />
                        <div className={clsx("content", visibilityClasses)}>
                            <Outlet />
                        </div>
                    </div>
                }>
                    <Route index element={<NavigateToHomeOrDefaultPage defaultPageUrl={defaultPageUrl} />} />
                    <Route path="storylines/:id" element={<RenderStoryline loader={ensureCorrectStorylineIsLoaded(loadStoryline)} />} />
                    <Route path="*" element={<NotFound />} />
                </Route>
            </Routes>
        </JsxParserComponentsContext.Provider>
    );
}

export default React.memo(Viewer);
